import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

const sharedGetCache = new Map();
const CACHEABLE_GET_PREFIXES = [
  "/schools", "/classes", "/semesters", "/academic-stages", "/disciplines",
  "/memberships", "/administration/capabilities", "/questions", "/analytics/dashboard",
  "/analytics/enamed-profile", "/analytics/medcof", "/admin/credits/accounts", "/student-stamps",
];

function clearSharedGetCache() {
  sharedGetCache.clear();
}

function cacheableGet(url, config = {}) {
  if (config?.responseType || !CACHEABLE_GET_PREFIXES.some((prefix) => String(url).startsWith(prefix))) return false;
  return true;
}

const rawGet = api.get.bind(api);
api.get = (url, config = {}) => {
  if (!cacheableGet(url, config)) return rawGet(url, config);
  const key = `${url}|${JSON.stringify(config?.params || {})}`;
  const now = Date.now();
  const current = sharedGetCache.get(key);
  if (current?.response && current.expiresAt > now) return Promise.resolve(current.response);
  if (current?.promise) return current.promise;
  const ttl = String(url).includes("/analytics/") ? 90000 : 30000;
  const promise = rawGet(url, config).then((response) => {
    sharedGetCache.set(key, { response, expiresAt: Date.now() + ttl });
    return response;
  }).catch((error) => {
    sharedGetCache.delete(key);
    throw error;
  });
  sharedGetCache.set(key, { promise, expiresAt: now + ttl });
  return promise;
};

function readCookie(name) {
  const prefix = `${encodeURIComponent(name)}=`;
  const part = document.cookie.split("; ").find((item) => item.startsWith(prefix));
  return part ? decodeURIComponent(part.slice(prefix.length)) : "";
}

function csrfToken() {
  return readCookie("__Host-sakix_csrf") || readCookie("sakix_csrf");
}

api.interceptors.request.use((config) => {
  const method = String(config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(method)) {
    clearSharedGetCache();
    const token = csrfToken();
    if (token) config.headers["X-CSRF-Token"] = token;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = String(error?.config?.url || "");
    const publicAuthRequest = [
      "/auth/login", "/auth/register", "/auth/verify", "/auth/resend-code",
      "/auth/password/forgot", "/auth/password/reset", "/auth/mfa/verify",
      "/auth/mfa/setup/start", "/auth/mfa/setup/confirm",
    ].some((path) => url.includes(path));
    if (status === 401 && !publicAuthRequest) {
      window.dispatchEvent(new Event("sakix-auth-expired"));
    }
    return Promise.reject(error);
  }
);

/* ------------- Auth Context ------------- */
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = anonymous, obj = auth

  const refreshMe = async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      return res.data;
    } catch {
      setUser(null);
      return null;
    }
  };

  useEffect(() => {
    const initialize = async () => {
      try { await api.get("/auth/csrf"); } catch { /* cookie bootstrap is best-effort */ }
      await refreshMe();
    };
    const expire = () => setUser(null);
    window.addEventListener("sakix-auth-expired", expire);
    initialize();
    return () => window.removeEventListener("sakix-auth-expired", expire);
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export function fmtError(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Erro desconhecido";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" · ");
  if (typeof d === "object") {
    const message = d.message || d.error || "Operacao nao concluida";
    const creditInfo = d.code === "insufficient_credits"
      ? ` (necessarios: ${d.required || 0}; disponiveis: ${d.available || 0})`
      : "";
    return `${message}${creditInfo}`;
  }
  return String(d);
}
